import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Nav, Row, Spinner } from "react-bootstrap";
import {
  fetchIntegrationMappings,
  patchIntegrationMapping,
  postIntegrationMapping,
} from "../../utils/http/companies";
import {
  CONDITION_OPS,
  EMPTY_RENTIO_FORM,
  RENTIO_ID_FIELD,
  TRANSFORMS,
  UrlCondition,
  UrlRule,
  UrlRuleSet,
  UrlRulesMappings,
  RentioForm,
  RentioIntegration,
  matchingRuleIndex,
  rentioFormToRuleSet,
  renderPattern,
  ruleSetToRentioForm,
  validateRuleSet,
} from "../../utils/urlRules";

interface IntegrationUrlRulesProps {
  companyNameOrId: string;
  integrationName: string;
}

type SubView = "listing_url" | "rentio_url";

interface UrlField {
  token: string;
  example: string | number;
}

// Fields of the stored CRM record that rules can use, with an example value. `_offer` ("sale" or "rent")
// and `_city` are derived by Windmill.
const FIELDS: Record<string, UrlField[]> = {
  zabun: [
    { token: "property_id", example: 4182731 },
    { token: "title.nl", example: "Ruime woning met tuin" },
    { token: "_city", example: "Lier" },
    { token: "_offer", example: "sale" },
    { token: "transaction_id", example: 1 },
    { token: "office_autoid", example: 32454 },
  ],
  whise: [
    { token: "id", example: 7243982 },
    { token: "referenceNumber", example: "7212968" },
    { token: "purposeStatus.name", example: "for sale" },
    { token: "_offer", example: "sale" },
  ],
  omnicasa: [
    { token: "ID", example: 1331840 },
    { token: "WebIDName", example: "Appartement" },
    { token: "City", example: "Hamme" },
    { token: "Zip", example: "9220" },
    { token: "_offer", example: "sale" },
  ],
  zabun360: [
    { token: "id", example: 8458825 },
    { token: "reference", example: "REF-1" },
    { token: "_offer", example: "sale" },
  ],
};

const RENTIO_INTEGRATIONS: string[] = ["zabun", "whise", "omnicasa"];

function buildSampleContext(fields: UrlField[]): Record<string, unknown> {
  const context: Record<string, unknown> = {};
  for (const { token, example } of fields) {
    const keys = token.split(".");
    let target = context;
    keys.slice(0, -1).forEach((key) => {
      target = (target[key] ??= {}) as Record<string, unknown>;
    });
    target[keys[keys.length - 1]] = example;
  }
  return context;
}

// Add a transform to the last placeholder of a pattern, e.g. "{_city}" + "|slug" -> "{_city|slug}".
function appendTransform(pattern: string, transform: string): string {
  const close = pattern.lastIndexOf("}");
  return close === -1 ? pattern : pattern.slice(0, close) + transform + pattern.slice(close);
}

function describeRule(rule: UrlRule): string {
  const conditions = rule.when ?? [];
  if (conditions.length === 0) return "Applies to every listing no rule above matched";
  return `Applies when ${conditions.length} condition${conditions.length === 1 ? "" : "s"} hold`;
}

interface RuleEditorProps {
  ruleSet: UrlRuleSet;
  fields: UrlField[];
  onChange: (ruleSet: UrlRuleSet) => void;
}

function RuleEditor({ ruleSet, fields, onChange }: RuleEditorProps) {
  const [activeRule, setActiveRule] = useState(0);
  const sample = useMemo(() => buildSampleContext(fields), [fields]);
  const sampleMatch = matchingRuleIndex(ruleSet, sample);
  const fieldTokens = fields.map((f) => f.token);

  const update = (index: number, patch: Partial<UrlRule>) =>
    onChange({ rules: ruleSet.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  const updateCondition = (ruleIndex: number, condIndex: number, patch: Partial<UrlCondition>) =>
    update(ruleIndex, {
      when: (ruleSet.rules[ruleIndex].when ?? []).map((c, i) => (i === condIndex ? { ...c, ...patch } : c)),
    });
  const move = (index: number, delta: number) => {
    const rules = [...ruleSet.rules];
    [rules[index], rules[index + delta]] = [rules[index + delta], rules[index]];
    onChange({ rules });
    setActiveRule(index + delta);
  };
  const insertIntoActive = (build: (pattern: string) => string) => {
    const rule = ruleSet.rules[activeRule];
    if (rule && rule.function == null) update(activeRule, { pattern: build(rule.pattern ?? "") });
  };

  const renderRule = (rule: UrlRule, index: number) => {
    const translations = Object.entries(rule.translations ?? {});
    const usesMap = (rule.pattern ?? "").includes("|map");
    const example = rule.pattern ? renderPattern(rule.pattern, sample, rule.translations ?? {}) : null;
    return (
      <Card
        key={index}
        className="mb-3"
        border={index === activeRule ? "primary" : undefined}
        onClick={() => setActiveRule(index)}
      >
        <Card.Body>
          <div className="d-flex align-items-start gap-2 mb-3">
            <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1" style={{ minWidth: 0 }}>
              <Badge bg="dark">Rule {index + 1}</Badge>
              {sampleMatch === index && (
                <Badge bg="success" title="This rule matches the example listing">
                  Matches example
                </Badge>
              )}
              <span className="text-muted small">{describeRule(rule)}</span>
            </div>
            <div className="d-flex gap-1 flex-shrink-0">
              <Button
                variant="outline-secondary"
                size="sm"
                aria-label="Move rule up"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <i className="bi bi-arrow-up" />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                aria-label="Move rule down"
                disabled={index === ruleSet.rules.length - 1}
                onClick={() => move(index, 1)}
              >
                <i className="bi bi-arrow-down" />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                aria-label="Delete rule"
                onClick={() => {
                  onChange({ rules: ruleSet.rules.filter((_, i) => i !== index) });
                  setActiveRule(0);
                }}
              >
                <i className="bi bi-trash" />
              </Button>
            </div>
          </div>

          <div className="fw-semibold small mb-2">When</div>
          {(rule.when ?? []).length === 0 && (
            <div className="text-muted small border rounded p-2 mb-2">
              Always. This rule catches every listing that no rule above it matched.
            </div>
          )}
          {(rule.when ?? []).map((condition, condIndex) => {
            const needsValue = CONDITION_OPS.find((o) => o.value === condition.op)?.needsValue;
            return (
              <div key={condIndex} className="d-flex gap-2 align-items-center flex-wrap mb-2">
                <span className="text-muted small" style={{ width: 32 }}>
                  {condIndex === 0 ? "If" : "and"}
                </span>
                <Form.Select
                  size="sm"
                  aria-label="Field"
                  className="font-monospace"
                  style={{ flex: "1 1 160px" }}
                  value={condition.field}
                  onChange={(e) => updateCondition(index, condIndex, { field: e.target.value })}
                >
                  {!fieldTokens.includes(condition.field) && <option value={condition.field}>{condition.field}</option>}
                  {fieldTokens.map((token) => (
                    <option key={token} value={token}>
                      {token}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  aria-label="Operator"
                  style={{ flex: "1 1 140px" }}
                  value={condition.op}
                  onChange={(e) =>
                    updateCondition(index, condIndex, { op: e.target.value as UrlCondition["op"] })
                  }
                >
                  {CONDITION_OPS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
                {needsValue && (
                  <Form.Control
                    size="sm"
                    aria-label="Value"
                    style={{ flex: "2 1 160px" }}
                    value={condition.value ?? ""}
                    onChange={(e) => updateCondition(index, condIndex, { value: e.target.value })}
                  />
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  aria-label="Remove condition"
                  onClick={() => update(index, { when: (rule.when ?? []).filter((_, i) => i !== condIndex) })}
                >
                  <i className="bi bi-x-lg" />
                </Button>
              </div>
            );
          })}
          <div className="mb-3">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              update(index, { when: [...(rule.when ?? []), { field: fieldTokens[0] ?? "", op: "contains", value: "" }] })
            }
          >
            + Add condition
          </Button>
          </div>

          {rule.function != null ? (
            <Alert variant="secondary" className="mb-0">
              This rule uses the coded function <code>{rule.function}</code>, registered in Windmill.
            </Alert>
          ) : (
            <>
              <Form.Label className="fw-semibold small">Build URL</Form.Label>
              <Form.Control
                className="font-monospace"
                value={rule.pattern ?? ""}
                onFocus={() => setActiveRule(index)}
                onChange={(e) => update(index, { pattern: e.target.value })}
                placeholder="https://example.be/aanbod/{property_id}"
              />
              <div className="text-muted small mt-1">
                Example:{" "}
                {example ? (
                  <span className="font-monospace text-body" style={{ wordBreak: "break-all" }}>
                    {example}
                  </span>
                ) : (
                  <span>no URL for the example listing (a field is empty or has no translation)</span>
                )}
              </div>
            </>
          )}

          {usesMap && (
            <div className="border-top mt-3 pt-3">
              <div className="fw-semibold small">Translations for |map</div>
              <div className="text-muted small mb-2">The CRM value on the left is replaced by the text on the right.</div>
              {translations.map(([from, to], i) => (
                <div key={i} className="d-flex gap-2 align-items-center mb-2">
                  <Form.Control
                    size="sm"
                    aria-label="CRM value"
                    value={from}
                    onChange={(e) =>
                      update(index, {
                        translations: Object.fromEntries(
                          translations.map(([f, t], j) => (j === i ? [e.target.value, t] : [f, t])),
                        ),
                      })
                    }
                  />
                  <span aria-hidden="true">→</span>
                  <Form.Control
                    size="sm"
                    aria-label="URL text"
                    className="font-monospace"
                    value={to}
                    onChange={(e) =>
                      update(index, {
                        translations: Object.fromEntries(
                          translations.map(([f, t], j) => (j === i ? [f, e.target.value] : [f, t])),
                        ),
                      })
                    }
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    aria-label="Remove translation"
                    onClick={() =>
                      update(index, { translations: Object.fromEntries(translations.filter((_, j) => j !== i)) })
                    }
                  >
                    <i className="bi bi-x-lg" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => update(index, { translations: { ...(rule.translations ?? {}), "": "" } })}
              >
                + Add translation
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Row className="g-4">
      <Col xl={8}>
        <p className="text-muted small">
          Rules are checked from top to bottom. The first rule that matches a listing builds its URL. If no rule
          matches, the listing gets no URL.
        </p>
        {ruleSet.rules.length === 0 && <Alert variant="secondary">No rules: listings get no URL.</Alert>}
        {ruleSet.rules.map(renderRule)}
        <Button
          variant="outline-primary"
          onClick={() => {
            onChange({ rules: [...ruleSet.rules, { when: [], pattern: "" }] });
            setActiveRule(ruleSet.rules.length);
          }}
        >
          + Add rule
        </Button>
      </Col>
      <Col xl={4}>
        <Card>
          <Card.Body>
            <div className="fw-semibold">Fields</div>
            <div className="text-muted small mb-2">
              Click a field to add it to <strong>Rule {activeRule + 1}</strong>. <code>_offer</code> is sale or rent.
            </div>
            <div className="d-grid gap-1 mb-3">
              {fields.map((f) => (
                <Button
                  key={f.token}
                  variant="outline-secondary"
                  size="sm"
                  className="d-flex justify-content-between text-start"
                  onClick={() => insertIntoActive((pattern) => `${pattern}{${f.token}}`)}
                >
                  <span className="font-monospace text-primary">{f.token}</span>
                  <span className="text-muted text-truncate ms-2">{String(f.example)}</span>
                </Button>
              ))}
            </div>
            <div className="fw-semibold">Transforms</div>
            <div className="text-muted small mb-2">Added to the last field in the selected rule.</div>
            <div className="d-grid gap-1">
              {TRANSFORMS.map((t) => (
                <Button
                  key={t.token}
                  variant="outline-secondary"
                  size="sm"
                  className="d-flex justify-content-between text-start"
                  onClick={() => insertIntoActive((pattern) => appendTransform(pattern, t.token))}
                >
                  <span className="font-monospace text-primary">{t.token}</span>
                  <span className="text-muted ms-2">{t.note}</span>
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

interface RentioFormViewProps {
  integration: RentioIntegration;
  form: RentioForm;
  onChange: (form: RentioForm) => void;
}

function RentioFormView({ integration, form, onChange }: RentioFormViewProps) {
  const idField = RENTIO_ID_FIELD[integration];
  const perOfficeAllowed = integration === "zabun";
  const subdomain = form.mode === "single" ? form.single || "subdomain" : "subdomain";

  return (
    <div style={{ maxWidth: 720 }}>
      <Form.Check
        type="switch"
        id="rentio-enabled"
        className="mb-3"
        label={<strong>Rentio links</strong>}
        checked={form.enabled}
        onChange={(e) => onChange({ ...form, enabled: e.target.checked })}
      />
      <div className="text-muted small mb-3">
        Show a Rentio link in automations for this company&apos;s rental listings. Only listings for rent get a link.
      </div>
      {form.enabled && (
        <>
          <div className="fw-semibold small mb-1">The link looks like</div>
          <div className="font-monospace border rounded bg-light p-2 mb-3" style={{ wordBreak: "break-all" }}>
            https://<span className="text-primary">{subdomain}</span>.mijnhuurprofiel.be/{integration}/
            <span className="text-muted">{`{${idField}}`}</span>
          </div>

          <Form.Check
            type="radio"
            id="rentio-single"
            name="rentio-mode"
            className="mb-2"
            label={<strong>Same for all offices</strong>}
            checked={form.mode === "single"}
            onChange={() => onChange({ ...form, mode: "single" })}
          />
          {form.mode === "single" && (
            <div className="d-flex align-items-center gap-2 ps-4 mb-3 flex-wrap">
              <span className="font-monospace text-muted">https://</span>
              <Form.Control
                aria-label="Subdomain"
                className="font-monospace"
                style={{ maxWidth: 220 }}
                value={form.single}
                onChange={(e) => onChange({ ...form, single: e.target.value })}
              />
              <span className="font-monospace text-muted">.mijnhuurprofiel.be</span>
            </div>
          )}
          {perOfficeAllowed && (
            <>
              <Form.Check
                type="radio"
                id="rentio-office"
                name="rentio-mode"
                className="mb-2"
                label={<strong>Different per office</strong>}
                checked={form.mode === "office"}
                onChange={() => onChange({ ...form, mode: "office" })}
              />
              {form.mode === "office" && (
                <div className="ps-4">
                  {form.offices.map((office, i) => (
                    <div key={i} className="d-flex gap-2 align-items-center mb-2">
                      <Form.Control
                        aria-label="Office id in Zabun"
                        placeholder="Office id in Zabun (office_autoid)"
                        value={office.id}
                        onChange={(e) =>
                          onChange({
                            ...form,
                            offices: form.offices.map((o, j) => (j === i ? { ...o, id: e.target.value } : o)),
                          })
                        }
                      />
                      <Form.Control
                        aria-label="Subdomain"
                        className="font-monospace"
                        placeholder="subdomain"
                        value={office.sub}
                        onChange={(e) =>
                          onChange({
                            ...form,
                            offices: form.offices.map((o, j) => (j === i ? { ...o, sub: e.target.value } : o)),
                          })
                        }
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Remove office"
                        onClick={() => onChange({ ...form, offices: form.offices.filter((_, j) => j !== i) })}
                      >
                        <i className="bi bi-x-lg" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => onChange({ ...form, offices: [...form.offices, { id: "", sub: "" }] })}
                  >
                    + Add office
                  </Button>
                  <div className="text-muted small mt-2">
                    Listings of an office that is not listed here, or has no subdomain, get no Rentio link.
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function IntegrationUrlRules({ companyNameOrId, integrationName }: IntegrationUrlRulesProps) {
  const queryClient = useQueryClient();
  const fields = FIELDS[integrationName] ?? [];
  const rentioIntegration = RENTIO_INTEGRATIONS.includes(integrationName) ? (integrationName as RentioIntegration) : null;

  const [subView, setSubView] = useState<SubView>("listing_url");
  const [draft, setDraft] = useState<UrlRulesMappings>({});
  const [rentioForm, setRentioForm] = useState<RentioForm | "custom">({ ...EMPTY_RENTIO_FORM });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["integration-mappings", companyNameOrId, integrationName],
    queryFn: ({ signal }) => fetchIntegrationMappings({ companyNameOrId, integrationName, signal }),
  });

  const existing = data?.find((m) => m.mapping_type === "url_rules") ?? null;
  const stored = useMemo(() => (existing?.mappings ?? {}) as unknown as UrlRulesMappings, [existing]);

  // (Re)load the draft whenever the stored mapping changes (first load, after a save).
  useEffect(() => {
    setDraft(structuredClone(stored));
    setRentioForm(rentioIntegration ? ruleSetToRentioForm(stored.rentio_url, rentioIntegration) : "custom");
  }, [stored, rentioIntegration]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);

  const setRuleSet = (type: SubView, ruleSet: UrlRuleSet) => {
    setDraft((prev) => ({ ...prev, [type]: ruleSet }));
    setSaved(false);
  };

  const handleSave = async () => {
    const errors = (Object.entries(draft) as [SubView, UrlRuleSet][]).flatMap(([type, ruleSet]) =>
      validateRuleSet(ruleSet).map((e) => `${type === "listing_url" ? "Listing URL" : "Rentio URL"}, ${e}`),
    );
    if (errors.length > 0) {
      setSaveError(errors.join(" "));
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const mappings = draft as Record<string, unknown>;
      if (existing?.id) {
        await patchIntegrationMapping({ companyNameOrId, integrationName, mappingId: existing.id, update: { mappings } });
      } else {
        await postIntegrationMapping({ companyNameOrId, integrationName, mapping: { mapping_type: "url_rules", mappings } });
      }
      await queryClient.invalidateQueries({ queryKey: ["integration-mappings", companyNameOrId, integrationName] });
      setSaved(true);
    } catch (err) {
      setSaveError((err as Error)?.message ?? "An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  if (error) return <Alert variant="danger">Failed to load the URL rules. Please try again.</Alert>;
  if (isLoading || !data) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  const listingRules = draft.listing_url ?? { rules: [] };
  const rentioRules = draft.rentio_url ?? { rules: [] };

  return (
    <>
      <Nav variant="pills" className="mb-4" activeKey={subView} onSelect={(k) => setSubView((k as SubView) ?? "listing_url")}>
        <Nav.Item>
          <Nav.Link eventKey="listing_url">Listing URL</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="rentio_url">Rentio URL</Nav.Link>
        </Nav.Item>
      </Nav>

      {subView === "listing_url" && (
        <RuleEditor ruleSet={listingRules} fields={fields} onChange={(rs) => setRuleSet("listing_url", rs)} />
      )}

      {subView === "rentio_url" &&
        (rentioIntegration && rentioForm !== "custom" ? (
          <RentioFormView
            integration={rentioIntegration}
            form={rentioForm}
            onChange={(form) => {
              setRentioForm(form);
              setRuleSet("rentio_url", rentioFormToRuleSet(form, rentioIntegration));
            }}
          />
        ) : (
          <>
            <Alert variant="secondary">
              {rentioIntegration
                ? "These Rentio rules are more detailed than the simple form, so they are edited as rules."
                : "Rentio links for this CRM are edited as rules."}
            </Alert>
            <RuleEditor ruleSet={rentioRules} fields={fields} onChange={(rs) => setRuleSet("rentio_url", rs)} />
          </>
        ))}

      <Card className="mt-4">
        <Card.Body className="d-flex gap-3 align-items-center flex-wrap">
          <div className="flex-grow-1" style={{ flexBasis: 360 }}>
            {saved && !dirty && (
              <Alert variant="success" className="py-2">
                Saved. Windmill is rebuilding the URLs for all listings of this company from stored data.
              </Alert>
            )}
            {saveError && (
              <Alert variant="danger" className="py-2">
                {saveError}
              </Alert>
            )}
            <span className="small text-muted">
              Saving rebuilds the URLs of every listing of this company from data we already have. The CRM is not
              called.
            </span>
          </div>
          <Button variant="primary" disabled={!dirty || isSaving} onClick={handleSave}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Card.Body>
      </Card>
    </>
  );
}

export default IntegrationUrlRules;
